import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, Mail, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('email');
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        const res = await api.post('/auth/register', { email, password, name });
        if (res.data.approved === false) {
          setError('Account created! Your account is pending admin approval. You will be notified when approved.');
          setMode('login');
          setLoading(false);
          return;
        }
        login(res.data);
        toast.success('Account created');
      } else {
        const res = await api.post('/auth/login-email', { email, password });
        login(res.data);
        toast.success('Logged in');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]" data-testid="login-page">
      <div className="bg-white border border-[#E5E7EB] p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/triomics-logo.png" alt="Triomics" className="h-8" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Data Dictionary
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">Data Dictionary Management Platform</p>
        </div>

        {/* Auth method tabs */}
        <div className="flex gap-px bg-[#E5E7EB] mb-6" data-testid="auth-tabs">
          <button
            onClick={() => setTab('email')}
            className={`flex-1 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${tab === 'email' ? 'bg-white text-[#002FA7]' : 'bg-[#F9FAFB] text-[#9CA3AF]'}`}
            data-testid="tab-email"
          >
            <Mail className="w-3.5 h-3.5 inline mr-1.5" /> Email
          </button>
          <button
            onClick={() => setTab('google')}
            className={`flex-1 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${tab === 'google' ? 'bg-white text-[#002FA7]' : 'bg-[#F9FAFB] text-[#9CA3AF]'}`}
            data-testid="tab-google"
          >
            <LogIn className="w-3.5 h-3.5 inline mr-1.5" /> Google SSO
          </button>
        </div>

        {tab === 'google' && (
          <div>
            <Button
              onClick={handleGoogleLogin}
              className="w-full h-11 text-sm font-medium bg-[#0A0A0A] hover:bg-[#171717] text-white rounded-none"
              data-testid="google-login-btn"
            >
              <LogIn className="w-4 h-4 mr-2" /> Sign in with Google
            </Button>
            <p className="text-[10px] text-[#9CA3AF] mt-3 text-center">Requires authorized Google account</p>
          </div>
        )}

        {tab === 'email' && (
          <form onSubmit={handleEmailSubmit}>
            {error && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 mb-4" data-testid="auth-error">
                {error}
              </div>
            )}
            {mode === 'register' && (
              <div className="mb-3">
                <Label className="text-xs font-semibold text-[#4B5563]">Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className="rounded-none mt-1" data-testid="name-input" placeholder="Full name" required />
              </div>
            )}
            <div className="mb-3">
              <Label className="text-xs font-semibold text-[#4B5563]">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="rounded-none mt-1" data-testid="email-input" placeholder="you@company.com" required />
            </div>
            <div className="mb-4">
              <Label className="text-xs font-semibold text-[#4B5563]">Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="rounded-none mt-1" data-testid="password-input" placeholder="Min 6 characters" required minLength={6} />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-10 rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="submit-btn">
              {loading ? 'Please wait...' : mode === 'register' ? 'Create Account' : 'Sign In'}
            </Button>
            <p className="text-xs text-center mt-4 text-[#4B5563]">
              {mode === 'login' ? (
                <>No account? <button type="button" onClick={() => { setMode('register'); setError(''); }} className="text-[#002FA7] font-medium" data-testid="switch-register">Create one</button></>
              ) : (
                <>Have an account? <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-[#002FA7] font-medium" data-testid="switch-login">Sign in</button></>
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
